import { useState, useRef, useCallback } from 'react';
import { Send, ImagePlus, X, Loader2 } from 'lucide-react';
import { compressImage } from '../../lib/imageCompression';

interface ChatInputProps {
  onSend: (message: string, images?: Array<{ base64: string; mediaType: string }>) => void;
  disabled?: boolean;
}

export default function ChatInput({ onSend, disabled }: ChatInputProps) {
  const [text, setText] = useState('');
  const [images, setImages] = useState<Array<{ base64: string; mediaType: string; preview: string }>>([]);
  const [compressing, setCompressing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed && images.length === 0) return;
    if (disabled) return;

    const imageData = images.map(({ base64, mediaType }) => ({ base64, mediaType }));
    onSend(trimmed, imageData.length > 0 ? imageData : undefined);
    setText('');
    setImages([]);

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.focus();
      }
    }, 0);
  }, [text, images, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length > 4000) return;
    setText(value);

    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remaining = 3 - images.length;
    const toProcess = Array.from(files).slice(0, remaining);

    setCompressing(true);
    try {
      for (const file of toProcess) {
        const compressed = await compressImage(file);
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve) => {
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(compressed);
        });

        const preview = URL.createObjectURL(compressed);
        setImages(prev => [...prev, {
          base64,
          mediaType: compressed.type || 'image/jpeg',
          preview,
        }]);
      }
    } finally {
      setCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="border-t border-theme-border p-3">
      {images.length > 0 && (
        <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <div key={i} className="relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-theme-border">
              <img src={img.preview} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(i)}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || images.length >= 3 || compressing}
          className="flex-shrink-0 p-2 text-theme-secondary hover:text-brand transition-colors disabled:opacity-40"
          title="Afbeelding toevoegen (max 3)"
        >
          {compressing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleImageSelect}
        />

        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Stel een vraag..."
          rows={1}
          className="flex-1 resize-none bg-theme-surface border border-theme-border rounded-xl px-3 py-2 text-sm text-theme-primary placeholder:text-theme-tertiary focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 disabled:opacity-50"
          style={{ maxHeight: 120 }}
        />

        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || (!text.trim() && images.length === 0)}
          className="flex-shrink-0 p-2 bg-brand text-white rounded-xl hover:bg-brand-active transition-colors disabled:opacity-40"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[10px] text-theme-tertiary">
          {text.length > 0 && `${text.length}/4000`}
        </span>
        <span className="text-[10px] text-theme-tertiary">
          Shift+Enter voor nieuwe regel
        </span>
      </div>
    </div>
  );
}
